import { Suspense, useEffect } from 'react';

import {
  SubscriptionClient,
  loadGSIScriptPropFn,
} from '@skyscanner-internal/global-components/google-one-tap/helpers';
import { isYoloReadyToDisplay } from '@web-platform/shared-banana-redux/src/state';
import {
  CONFIG_SERVICE_KEYS,
  FEATURE_DECISION_KEYS,
} from '@web-platform/shared-universal-constants-config';
import { LOGIN_MODAL_REFERRERS } from '@web-platform/shared-universal-features-global-login-form';
import { loggedInStatusChanged } from '@web-platform/shared-universal-redux/src/actions/user';
import { getCurrentScreenName } from '@web-platform/shared-universal-redux/src/selectors/trustedFunnel';
import { getLogin, getUserIdentifiers } from '@web-platform/shared-universal-redux/src/state';
import {
  trackSmartMetric,
  trackChokePoint,
} from '@web-platform/shared-universal-tracking/src/trackExperiment';
import { translate } from '@web-platform/shared-universal-translations';
import ErrorBoundary from '@web-platform/shared-universal-ui-error-boundary';
import {
  getConfigServiceValue,
  getFeatureDecision,
} from '@web-platform/shared-universal-utils-frontend/src/config';
import { lazyWithRetry } from '@web-platform/shared-universal-utils-frontend/src/helpers/lazyLoading';
import { getGoogleOneTapAuthClient } from '@web-platform/shared-universal-utils-frontend/src/singletons/authClient';
import { getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';
import { useDispatch, useSelector } from 'react-redux';
import { logError, logOperationalEvent, logWarn } from 'saddlebag-logger';

const GoogleOneTapLazy = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "GoogleOneTapGlobalComponent" */
      '@skyscanner-internal/global-components/google-one-tap'
    ),
);

const AUTH_CONSUMER = 'banana';

const GoogleOneTapInner = () => {
  const dispatch = useDispatch();
  const { locale, market } = getCulture();
  const isGoogleOneTapReady = useSelector(isYoloReadyToDisplay);
  const { isOpen: isLoginModalOpen, referrer } = useSelector(getLogin);
  const { isLoggedIn } = useSelector(getUserIdentifiers);
  const isAutoPromptOpen = isLoginModalOpen && referrer === LOGIN_MODAL_REFERRERS.AUTO_PROMPT;
  const isProductionEnv = process.env.NODE_ENV === 'production';

  const isGoogleOneTapEnabled = getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_GOOGLE_ONE_TAP);
  const isValidBrowser = getFeatureDecision(FEATURE_DECISION_KEYS.IS_VALID_BROWSER_FOR_GOT);
  const isInRemoveChromeRestrictionExperiment = getConfigServiceValue(
    CONFIG_SERVICE_KEYS.EXPERIMENT.REMOVE_CHROME_RESTRICTION_GOT,
  );
  useEffect(() => {
    trackChokePoint('users_who_saw_google_one_tap');
  }, []);
  const experimentContext = [];

  if (isInRemoveChromeRestrictionExperiment) {
    experimentContext.push('REMOVE_CHROME_RESTRICTION');
  }

  const authClient = getGoogleOneTapAuthClient();
  const clientId = getConfigServiceValue(
    isProductionEnv
      ? CONFIG_SERVICE_KEYS.OPS.GOOGLE_CLIENT_ID_PROD
      : CONFIG_SERVICE_KEYS.OPS.GOOGLE_CLIENT_ID_DEV,
  );

  const subscriptionClient = new SubscriptionClient();

  const currentScreenName = useSelector(getCurrentScreenName);

  if (
    isGoogleOneTapReady &&
    isGoogleOneTapEnabled &&
    isValidBrowser &&
    !isAutoPromptOpen &&
    !isLoggedIn &&
    clientId
  ) {
    return (
      <GoogleOneTapLazy
        authClient={authClient}
        clientId={clientId}
        consumer={AUTH_CONSUMER}
        experimentContext={experimentContext.join(',')}
        isProductionEnv={isProductionEnv}
        loadGSIScriptPropFn={loadGSIScriptPropFn}
        locale={locale}
        logger={{
          logOperationalEvent,
          logError,
          logWarn,
        }}
        market={market}
        onLogin={() => {
          trackSmartMetric('users_who_logged_in_with_one_tap');
          dispatch(loggedInStatusChanged(true));
        }}
        screenName={currentScreenName}
        strings={{
          ariaLabelClose: translate('CLOSE_MODAL'),
          cancellationMessage: translate('YOLO_CANCELLATION_MESSAGE'),
          errorMessage: translate('ERROR_MESSAGE_GENERIC'),
          errorHeading: translate('GOT_ERROR_MESSAGE_HEADING'),
          errorBody: translate('GOT_ERROR_MESSAGE_BODY'),
          cancellationHeading: translate('GOT_CANCELLATION_MESSAGE_HEADING'),
          cancellationBody: translate('GOT_CANCELLATION_MESSAGE_BODY'),
          loadingText: translate('YOLO_LOADING_TEXT'),
          loginButton: translate('GOT_ERROR_CTA'),
          loginSuccessMessage: translate('LOGIN_SUCCESS_FUL_LOGGED_IN_USER'),
          retryButton: translate('YOLO_RETRY_BUTTON'),
          marketingConsentDisclaimerText: translate('DISCLAIMER_TEXT_V5'),
          marketingConsentHeading: translate('S2L_MarketingOptIn_Heading'),
          marketingConsentPrimaryButton: translate('S2L_MarketingOptIn_PrimaryButton'),
          marketingConsentSecondaryButton: translate('S2L_MarketingOptIn_SecondaryButton'),
          marketingConsentSubHeadingApp: translate('S2L_MarketingOptIn_SubHeading_App'),
          marketingConsentStatusMessage: translate('STATUS_MESSAGE_TEXT'),
        }}
        subscriptionClient={subscriptionClient}
      />
    );
  }

  return null;
};

export const GoogleOneTap = () => (
  <ErrorBoundary componentName="GoogleOneTapLazy" fallback={null}>
    <Suspense fallback={null}>
      <GoogleOneTapInner />
    </Suspense>
  </ErrorBoundary>
);
